import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';

import { Eye, EyeOff, RefreshCw, Copy, Check, ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { toast } from 'sonner';

// Fonction pour calculer la force du mot de passe
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, level: 'none', label: '', color: '' };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    lengthBonus: password.length >= 12,
    lengthMax: password.length >= 16,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/`~]/.test(password),
    noCommon: !/(password|123456|azerty|qwerty|admin)/i.test(password),
  };

  if (checks.length) score += 15;
  if (checks.lengthBonus) score += 10;
  if (checks.lengthMax) score += 10;
  if (checks.lowercase) score += 15;
  if (checks.uppercase) score += 15;
  if (checks.numbers) score += 15;
  if (checks.special) score += 20;
  if (checks.noCommon) score += 0; else score -= 30;

  // Déterminer le niveau
  let level, label, color, bgColor, textColor;
  if (score < 30) {
    level = 'weak';
    label = 'Insuffisant';
    color = 'bg-red-500';
    bgColor = 'bg-red-50';
    textColor = 'text-red-700';
  } else if (score < 50) {
    level = 'fair';
    label = 'Faible';
    color = 'bg-orange-500';
    bgColor = 'bg-orange-50';
    textColor = 'text-orange-700';
  } else if (score < 70) {
    level = 'good';
    label = 'Moyen';
    color = 'bg-yellow-500';
    bgColor = 'bg-yellow-50';
    textColor = 'text-yellow-700';
  } else if (score < 85) {
    level = 'strong';
    label = 'Bon';
    color = 'bg-lime-500';
    bgColor = 'bg-lime-50';
    textColor = 'text-lime-700';
  } else {
    level = 'excellent';
    label = 'Excellent';
    color = 'bg-green-500';
    bgColor = 'bg-green-50';
    textColor = 'text-green-700';
  }

  return { score: Math.min(score, 100), level, label, color, bgColor, textColor, checks };
};

// Générateur de mot de passe sécurisé
const generateSecurePassword = (length = 16) => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  const allChars = lowercase + uppercase + numbers + special;
  
  // Assurer au moins un caractère de chaque type
  let password = '';
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  // Compléter avec des caractères aléatoires
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Mélanger le mot de passe
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const PasswordWithStrength = ({ value, onChange, required = true }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [strength, setStrength] = useState({ score: 0, level: 'none', label: '', color: '' });

  useEffect(() => {
    setStrength(calculatePasswordStrength(value));
  }, [value]);

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(16);
    onChange({ target: { value: newPassword } });
    setShowPassword(true); // Montrer le mot de passe généré
    toast.success('Mot de passe sécurisé généré !');
  };

  const handleCopyPassword = async () => {
    if (value) {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Mot de passe copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStrengthIcon = () => {
    if (strength.level === 'excellent' || strength.level === 'strong') {
      return <ShieldCheck className="h-4 w-4" />;
    } else if (strength.level === 'good' || strength.level === 'fair') {
      return <Shield className="h-4 w-4" />;
    } else {
      return <ShieldAlert className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="password">Mot de passe</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleGeneratePassword}
          className="text-xs text-blue-600 hover:text-blue-800 h-auto p-1"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Suggérer un mot de passe
        </Button>
      </div>
      
      <div className="relative">
        <Input
          id="password"
          type={showPassword ? 'text' : 'password'}
          data-testid="register-password-input"
          value={value}
          onChange={onChange}
          required={required}
          className="pr-20"
          placeholder="Entrez votre mot de passe"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCopyPassword}
              className="h-7 w-7 p-0"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4 text-gray-400" />
              )}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPassword(!showPassword)}
            className="h-7 w-7 p-0"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-gray-400" />
            ) : (
              <Eye className="h-4 w-4 text-gray-400" />
            )}
          </Button>
        </div>
      </div>

      {/* Indicateur de force */}
      {value && (
        <div className="space-y-2">
          {/* Barre de progression */}
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${strength.color}`}
              style={{ width: `${strength.score}%` }}
            />
          </div>
          
          {/* Label et indicateur */}
          <div className={`flex items-center justify-between px-2 py-1 rounded ${strength.bgColor}`}>
            <div className={`flex items-center gap-2 ${strength.textColor}`}>
              {getStrengthIcon()}
              <span className="text-sm font-medium">
                Sécurité : {strength.label}
              </span>
            </div>
            <span className={`text-xs ${strength.textColor}`}>
              {strength.score}%
            </span>
          </div>

          {/* Conseils */}
          {strength.level !== 'excellent' && strength.level !== 'strong' && (
            <div className="text-xs text-gray-500 space-y-1 mt-1">
              <p className="font-medium">Pour un mot de passe sécurisé :</p>
              <ul className="list-disc list-inside space-y-0.5 ml-1">
                {!strength.checks?.length && <li>Au moins 8 caractères</li>}
                {!strength.checks?.uppercase && <li>Une lettre majuscule</li>}
                {!strength.checks?.lowercase && <li>Une lettre minuscule</li>}
                {!strength.checks?.numbers && <li>Un chiffre</li>}
                {!strength.checks?.special && <li>Un caractère spécial (!@#$%...)</li>}
                {strength.checks?.length && !strength.checks?.lengthBonus && <li>12 caractères ou plus recommandés</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordWithStrength;
